/**
 * Zod schemas for runtime API response validation
 *
 * These schemas validate API responses at runtime in development
 * to catch contract violations before they cause bugs.
 *
 * USAGE: Only validate in development to avoid performance overhead.
 *
 * @example
 * if (process.env.NODE_ENV === 'development') {
 *   const result = AuthMeResponseSchema.safeParse(data);
 *   if (!result.success) {
 *     console.error('API Contract Violation:', result.error);
 *   }
 * }
 */
import { z } from 'zod';
// =============================================================================
// Auth Schemas
// =============================================================================
/**
 * Schema for GET /api/v1/auth/me response
 *
 * CRITICAL: This validates the auth endpoint that was involved in the
 * production bug where App.tsx expected data.data.userId but API
 * returned data.data.id.
 */
export const AuthMeDataSchema = z.object({
    // CRITICAL: Must be 'id', NOT 'userId'
    id: z.string().min(1, 'User ID must not be empty'),
    // CRITICAL: Must be actual email, NOT empty string
    email: z.string().email('Must be valid email').min(1, 'Email must not be empty'),
    // CRITICAL: Must be actual name, NOT empty string
    name: z.string().min(1, 'Name must not be empty'),
    role: z.enum(['admin', 'viewer']),
    familyId: z.string().min(1, 'Family ID must not be empty'),
    family: z
        .object({
        id: z.string(),
        name: z.string(),
        timezone: z.string(),
    })
        .nullable(),
    member: z
        .object({
        id: z.string(),
        name: z.string(),
        type: z.enum(['adult', 'teen', 'child']),
        color: z.string(),
        avatarUrl: z.string().nullable(),
    })
        .nullable(),
    connectedProviders: z.object({
        google: z.boolean(),
        microsoft: z.boolean(),
        googleKeep: z.boolean(),
    }),
    onboardingCompleted: z.boolean(),
    notificationSettings: z
        .object({
        pushEnabled: z.boolean(),
        dailyBriefingEnabled: z.boolean(),
        dailyBriefingTime: z.string(),
        travelTimeAlerts: z.boolean(),
    })
        .nullable(),
    createdAt: z.string(),
});
export const AuthMeResponseSchema = z.object({
    success: z.literal(true),
    data: AuthMeDataSchema,
});
// =============================================================================
// Member Schemas
// =============================================================================
/**
 * Schema for GET /api/v1/members/:id/connected-accounts response
 *
 * Validates the response shape that MemberCalendarActions expects.
 */
export const ConnectedAccountsDataSchema = z.object({
    google: z.object({
        count: z.number().int().nonnegative('Google count must be non-negative'),
        max: z.number().int().positive('Google max must be positive'),
    }),
    microsoft: z.object({
        count: z.number().int().nonnegative('Microsoft count must be non-negative'),
        max: z.number().int().positive('Microsoft max must be positive'),
    }),
});
export const ConnectedAccountsResponseSchema = z.object({
    success: z.literal(true),
    data: ConnectedAccountsDataSchema,
});
/**
 * Schema for family member entity
 */
export const FamilyMemberSchema = z.object({
    id: z.string(),
    familyId: z.string(),
    userId: z.string().nullable(),
    name: z.string().min(1),
    type: z.enum(['adult', 'teen', 'child']),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be valid hex color'),
    avatarUrl: z.string().url().nullable(),
    displayOrder: z.number().int().nonnegative(),
    searchKeywords: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const MembersListResponseSchema = z.object({
    success: z.literal(true),
    data: z.array(FamilyMemberSchema),
});
export const MemberDetailResponseSchema = z.object({
    success: z.literal(true),
    data: FamilyMemberSchema,
});
// =============================================================================
// Settings Schemas
// =============================================================================
/**
 * Schema for user display settings
 */
export const DisplaySettingsSchema = z.object({
    showCompletedChores: z.boolean(),
    defaultView: z.enum(['day', 'week', 'month']),
    timeFormat: z.enum(['12h', '24h']),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]),
});
export const SettingsResponseSchema = z.object({
    success: z.literal(true),
    data: DisplaySettingsSchema,
});
// =============================================================================
// Generic API Response Schema
// =============================================================================
/**
 * Generic API response schema factory
 */
export function createApiResponseSchema(dataSchema) {
    return z.object({
        success: z.literal(true),
        data: dataSchema,
    });
}
/**
 * Error response schema
 */
export const ApiErrorSchema = z.object({
    success: z.literal(false),
    error: z.union([
        z.string(),
        z.object({
            code: z.string(),
            message: z.string(),
            details: z.unknown().optional(),
        }),
    ]),
});
// =============================================================================
// Validation Helpers
// =============================================================================
/**
 * Validate API response in development mode only
 *
 * @example
 * const data = await fetch('/api/v1/auth/me').then(r => r.json());
 * validateInDev(AuthMeResponseSchema, data, '/auth/me');
 */
export function validateInDev(schema, data, endpoint) {
    // Only validate in development to avoid performance overhead
    if (typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development') {
        const result = schema.safeParse(data);
        if (!result.success) {
            console.error(`❌ API Contract Violation at ${endpoint}:`, result.error.format());
            console.error('Received data:', data);
            return undefined;
        }
        return result.data;
    }
    // In production, trust the data (types should already be checked by TS)
    return data;
}
/**
 * Create a validated fetch wrapper for an endpoint
 *
 * @example
 * const fetchAuthMe = createValidatedFetch('/api/v1/auth/me', AuthMeResponseSchema);
 * const data = await fetchAuthMe();
 */
export function createValidatedFetch(endpoint, schema) {
    return async (options) => {
        const response = await fetch(endpoint, options);
        const data = await response.json();
        return validateInDev(schema, data, endpoint);
    };
}
//# sourceMappingURL=api.js.map